<template>
  <div class="i-register">
    <iNavbarSimple />
    <iBackground :color="white" />

    <div class="wrap">
      <div
        v-if="!!OAUTH_FAILED"
        class="auth-failed"
      >
        Something went wrong! <strong>Please try again.</strong>
        <br />
        <small>
          <i>If the problem persists, contact our <a href="mailto:support@jobtest.org">support team</a>.</i>
        </small>
      </div>
      <div class="card">
        <div>
          <h1 class="title">Access Your Result</h1>
          <p class="subtitle">Enter your email to continue</p>
        </div>

        <iBaseButton
          class="cta"
          v-bind="OAUTH_BUTTON_STYLE"
          text="Continue with Google"
          :pre-icon="iconGoogle"
          @clicked="goToGoogleOauth()"
        />

        <div class="separator">
          <iBaseDelimiter class="delimiter" /><span>OR</span><iBaseDelimiter class="delimiter" />
        </div>

        <form
          novalidate
          @submit.prevent="submitForm()"
        >
          <div class="input-group">
            <label class="label">Email Address:</label>

            <div
              v-if="data.emailError"
              class="error-message"
            >
              {{ data.emailError }}
            </div>

            <input
              id="email"
              v-model="data.email"
              type="email"
              name="email"
              autocomplete="email"
              autocapitalize="off"
              spellcheck="false"
              class="input-field"
              maxlength="256"
              autofocus
            />
          </div>

          <div>
            <iBaseButton
              class="cta"
              v-bind="emailButtonStyle"
              @clicked="submitForm()"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRegisterUser } from "@/composables/useRegisterUser"
import { computed, reactive } from "vue"
import { useRouter } from "vue-router"

import api from "@/services/apiClient"

import { black, brandOpacity100, comet100, comet200, white } from "@/styles/colors"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseDelimiter from "@/components/base/BaseDelimiter.vue"
import iBackground from "@/components/global/Background.vue"
import iNavbarSimple from "@/components/global/NavbarSimple.vue"

import iconGoogle from "@/assets/icons/icon-google-color.svg"
import { GlobalVariables } from "@/helpers/globalVariables"

// Router
const router = useRouter()

// Composables
const registerUser = useRegisterUser()

// Refs
const data = reactive({
  emailError: "",
  submitting: false,
  email: ""
})

// CONSTANT
const SHARED_BUTTON_STYLES = {
  fontWeight: "500",
  width: "100%",
  borderRadius: "8px"
}

const OAUTH_BUTTON_STYLE = {
  ...SHARED_BUTTON_STYLES,
  textColor: black,
  backgroundColor: white,
  version: "outline",
  borderColor: comet100
}

// COMPUTED
const emailButtonStyle = computed(() => {
  return {
    ...SHARED_BUTTON_STYLES,
    isDisabled: !isValidEmail.value || data.submitting,
    textColor: !isValidEmail.value ? "white" : "black",
    backgroundColor: !isValidEmail.value ? comet200 : brandOpacity100,
    text: data.submitting ? "Please wait..." : "Continue with Email"
  }
})

const isValidEmail = computed(() => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i
  return emailPattern.test(data.email)
})

const OAUTH_FAILED = new URL(window.location.href).searchParams.get("oauthFailed")

// FUNCTIONS
/**
 * Redirects the user to the Google OAuth page.
 * After the user logs in with Google, they will be redirected back to the loading page.
 */
function goToGoogleOauth() {
  const search = window.location.search
  const redirectPath = GlobalVariables.urls.loading.path + search
  const failurePath = window.location.pathname + search
  api.auth.goToGoogleOauth(redirectPath, failurePath)
}

/**
 * Submits the form to register the user with their email.
 *
 * - If the email is valid, the user will be redirected to the loading page.
 * - If the email is invalid or already exists, an error message will be displayed.
 */
async function submitForm() {
  const email = data.email.trim()

  // Validate the email
  if (!isValidEmail.value) {
    data.emailError = "Please enter a valid email address."
    return
  }

  data.submitting = true
  data.emailError = "" // clear the error message

  const response = await registerUser(email)

  if (response.success) {
    router.push(GlobalVariables.urls.loading.path + window.location.search)
  } else {
    data.submitting = false

    if (response.code === "email-exists") {
      data.emailError = "This email is already in use. Please try another."
    } else {
      router.push({ name: GlobalVariables.urls.error.routeName })
    }
  }
}
</script>

<style scoped lang="scss">
$header-height: 60px;

.i-register {
  .wrap {
    margin: auto;
    padding: 16px;
    width: 100%;
    max-width: 560px;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 24px;
    }

    @media screen and (min-width: $breakpoint-md) {
      padding: 32px;
    }

    .auth-failed {
      background-color: $bittersweet300;
      color: $black;

      @include border-radius-md;

      text-align: center;
      padding: 10px 20px;
      margin-bottom: 12px;
    }

    .card {
      background-color: $white;
      text-align: start;
      box-shadow: #64646f14 0 7px 29px 0;
      padding: 24px 16px;

      @include border-radius-md;
      @include flex(column, $justify: stretch, $align: stretch, $gap: 24px);

      @media screen and (min-width: $breakpoint-sm) {
        gap: 28px;
        padding: 24px;
      }

      @media screen and (min-width: $breakpoint-md) {
        gap: 32px;
        padding: 32px;
      }

      .title {
        @include display-xs;

        font-weight: bold;
        margin: 0;
      }

      .subtitle {
        @include text-sm;

        font-weight: 400;
        margin: 8px 0 0;
      }

      .label {
        @include text-sm;

        font-weight: 600;
      }

      .input-field {
        min-height: 48px !important;
        margin-top: 10px;

        @include input-field-outline;
      }

      .error-message {
        color: $bittersweet500;
        margin-top: 8px;
        font-size: 14px;
      }
    }

    .separator {
      @include flex(row, $justify: space-around, $align: center);

      .delimiter {
        width: 40%;
      }
    }
  }
}
</style>
